import React, { useEffect } from "react";
import useDialog from "@/hooks/useDialog";
import { X } from "lucide-react";
import CompanyAsk from "../pages/CompanyAsk";
import { getVariant, COMPANY_FLOW_EXPERIMENT, COMPANY_FLOW_ARMS } from "@/lib/experiment";
import { trackEvent } from "@/lib/tracker";
import { gaTrack } from "@/lib/analytics";

/* The company funnel, opened over the landing page instead of replacing it.

   Sending somebody from a page full of people and proof to a bare form is a
   cliff, and the funnel numbers show them falling off it. In the native app the
   same flow reads fine because it opens from a screen that was never dense to
   begin with; on the web it followed a hero full of faces, so the form felt
   like the site had emptied out.

   So the page stays. It is dimmed and blurred behind the panel, which keeps the
   evidence in view while the questions get asked, and makes the whole thing
   feel like a step rather than a departure.

   CompanyDoors is rendered as-is rather than reimplemented. It is stage-driven
   and does not navigate on its own, so it behaves identically here and at
   /get-started; the same component, the same order, one funnel. It only leaves
   the overlay at the end, when the account is actually created, which is the
   one moment a full page is the right answer.

   sift_hero_need must be in session storage BEFORE this mounts: CompanyDoors
   reads it in a state initialiser and clears it. The caller sets it, then
   opens. */

export default function HeroFunnelModal({ open, onClose }) {
  // Focus in and back, Tab kept inside, Escape and the scroll lock: useDialog.
  const dialog = useDialog({ open, onClose });

  useEffect(() => {
    if (!open) return;
    trackEvent("hero_funnel_opened", { account_type: "organization" });
    // Mirrored to GA4: this is the step where the funnel actually begins on the
    // new hero, and without it GA4 shows a landing page and then an account with
    // nothing in between.
    gaTrack("hero_funnel_opened", { account_type: "organization" });
    // Meta gets ViewContent from hero_funnel_opened above: lib/analytics.js mirrorToMeta.
  }, [open]);

  if (!open) return null;

  return (
    <div className="hfm" role="presentation">
      <style>{CSS}</style>
      <button
        type="button"
        className="hfm__scrim"
        aria-label="Close and go back to the page"
        onClick={() => { trackEvent("hero_funnel_dismissed", { via: "scrim" }); gaTrack("hero_funnel_dismissed", { via: "scrim" }); onClose(); }}
      />
      <div
        className="hfm__panel"
        {...dialog.props}
        aria-labelledby={undefined}
        aria-label="Post what you need"
      >
        <button
          type="button"
          className="hfm__x"
          onClick={() => { trackEvent("hero_funnel_dismissed", { via: "close" }); gaTrack("hero_funnel_dismissed", { via: "close" }); onClose(); }}
        >
          <X size={18} strokeWidth={2.2} aria-hidden="true" />
          <span className="sr-only">Close</span>
        </button>
        <div className="hfm__body">
          {/* CompanyAsk replaced the doors (2026-10-04). It reads the hero's
              text from session storage and routes it without asking again. */}
          <CompanyAsk arm={getVariant(COMPANY_FLOW_EXPERIMENT, COMPANY_FLOW_ARMS, { primary: false })} onExit={onClose} />
        </div>
      </div>
    </div>
  );
}

const CSS = `
.hfm { position:fixed; inset:0; z-index:120; display:flex; align-items:center;
  justify-content:center; padding:24px 16px; }

/* Dimmed and blurred, not blacked out. The point is that the page is still
   there: the faces and the proof stay legible behind the questions. */
.hfm__scrim { position:absolute; inset:0; border:0; padding:0; cursor:pointer;
  background:rgba(27,24,19,.34);
  backdrop-filter:blur(7px) saturate(120%);
  -webkit-backdrop-filter:blur(7px) saturate(120%);
  animation:hfmFade .22s ease-out both; }
@keyframes hfmFade { from { opacity:0 } to { opacity:1 } }

.hfm__panel { position:relative; width:100%; max-width:680px; max-height:94vh;
  overflow:auto; -webkit-overflow-scrolling:touch;
  background:var(--surface); border:1px solid var(--border-strong);
  border-radius:var(--r-2xl); box-shadow:var(--shadow-xl); outline:none;
  animation:hfmRise .26s cubic-bezier(.22,.61,.36,1) both; }
@keyframes hfmRise { from { opacity:0; transform:translateY(14px) scale(.99) } to { opacity:1; transform:none } }

.hfm__x { position:absolute; top:12px; right:12px; z-index:2; width:34px; height:34px;
  display:grid; place-items:center; border-radius:999px; cursor:pointer;
  background:var(--surface); border:1px solid var(--border); color:var(--text-secondary);
  transition:border-color .2s, color .2s; }
.hfm__x:hover { border-color:var(--brand); color:var(--brand); }

/* The flow's screens are built as full-height pages wrapped around a centred
   surface card, which is right at /get-started and wrong inside a panel: the
   card lands inside the panel and you get a card in a card, two borders and two
   shadows around one form, with the content squeezed into 512px of a 660px
   dialog. Flattened here so the PANEL is the card. Scoped under .hfm__body, so
   the standalone route keeps its own shell exactly as it was. */
.hfm__body .min-h-screen { min-height:0 !important; padding:0 !important; }
.hfm__body .bg-bg { background:transparent !important; }
.hfm__body [class*="pb-[40vh]"] { padding-bottom:0 !important; }
.hfm__body > div > div {
  max-width:none !important; width:100% !important;
  background:transparent !important; border:0 !important; box-shadow:none !important;
  border-radius:0 !important; padding:26px 30px 30px !important; }
.hfm__body { padding:6px 0 2px; }

/* The mark belongs to the page, and the page is right there behind the panel.
   Repeating it inside the dialog is the site introducing itself to somebody who
   never left it, and it costs 60px at the top of every step. */
.hfm__body svg[width="44"], .hfm__body svg[height="44"] { display:none; }
.hfm__body > div > div > div:first-child:has(svg) { display:none; }

/* Fit the step in the panel rather than making them scroll for the button.
   The draft screen is the tall one: a textarea sized for a full page, three
   match rows and a callout. None of the content is cut -- it is the same
   fields, tightened -- so nothing has to be discovered by scrolling to know
   what the screen is asking. */
.hfm__body textarea { min-height:132px !important; }
.hfm__body .p-3 { padding:.55rem .7rem !important; }
.hfm__body .h-11 { height:1.9rem !important; }
.hfm__body .w-11 { width:1.9rem !important; }
.hfm__body .mt-5 { margin-top:.95rem !important; }
.hfm__body .mb-5 { margin-bottom:.7rem !important; }
.hfm__body .space-y-3 > * + * { margin-top:.4rem !important; }
.hfm__body .px-4.py-3 { padding:.55rem .8rem !important; font-size:0.8125rem !important; }

/* The action stays reachable whatever the content does. Tightening got the
   draft screen close to fitting, but "close" is not a guarantee across every
   viewport, font size and match count -- and a primary button you have to
   scroll to find is the one failure this panel cannot have. */
.hfm__body button[class*="rounded-pill"][class*="bg-brand"] {
  position:sticky; bottom:0; z-index:1; }
.hfm__body > div > div { padding-bottom:12px !important; }

@media (max-width:640px) {
  .hfm__body > div > div { padding:22px 20px 26px !important; }
  /* Space is scarcer, so let it scroll rather than squeezing the field to
     nothing -- a 90px description box is worse than a scrollbar. */
  .hfm__body textarea { min-height:130px !important; }
}

@media (max-width:640px) {
  .hfm { padding:0; align-items:flex-end; }
  /* Full-bleed sheet on a phone: a floating card with 16px of blurred page
     around it is neither a page nor a sheet. */
  .hfm__panel { max-width:none; max-height:94vh;
    border-radius:var(--r-2xl) var(--r-2xl) 0 0; border-bottom:0;
    padding-bottom:env(safe-area-inset-bottom); }
  @keyframes hfmRise { from { opacity:0; transform:translateY(28px) } to { opacity:1; transform:none } }
}

@media (prefers-reduced-motion: reduce) {
  .hfm__scrim, .hfm__panel { animation:none; }
}
`;
